import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { ApiService } from '../../utils/apiService';
import { InterfaceToSearchParams } from '../../utils/helper';
import { LifehackInterface } from '../../interface/lifehack';

type DataState = AbstractStateInterface<LifehackInterface>;

export interface LifehackSearch {
  id?: string;
}
const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export const findLifehackByCondition = createAsyncThunk(
  'admin-api/lifehack/get',
  async (payload: LifehackSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/lifehack?${searchParams}`, 'GET', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const getAllLifehacks = createAsyncThunk('admin-api/lifehack/all', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/lifehack/all`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const createLifehack = createAsyncThunk('admin-api/lifehack', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/lifehack`, 'POST', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const updateLifehack = createAsyncThunk('admin-api/lifehack/put', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/lifehack`, 'PUT', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const deleteLifehack = createAsyncThunk(
  'admin-api/lifehack/delete',
  async (payload: LifehackSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/lifehack?${searchParams}`, 'DELETE', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

const lifehackSlice = createSlice({
  name: 'lifehack',
  initialState,
  reducers: {
    resetLifehackSlice: () => {
      return { ...initialState };
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllLifehacks.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllLifehacks.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as LifehackInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(getAllLifehacks.rejected, (state) => {
        state.loading = false;
      })
      .addCase(findLifehackByCondition.pending, (state) => {
        state.loading = true;
      })
      .addCase(findLifehackByCondition.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.detail = action.payload.data[0] as LifehackInterface;
        } else {
          state.detail = null;
        }
      })
      .addCase(findLifehackByCondition.rejected, (state) => {
        state.loading = false;
      });
  },
});
export const { resetLifehackSlice, resetDetail } = lifehackSlice.actions;
export default lifehackSlice.reducer;
